import { css, cx } from "@emotion/css";
import {
	Avatar,
	CircularProgress,
	ListItemAvatar,
	ListItemSecondaryAction,
} from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import type React from "react";
import formatHtmlTag from "src/helpers/formatHtmlTag";
import { useFontSize } from "src/hooks/useFontSize";
import { AnswerType } from "src/types/enums";

interface AnswerProps {
	setAnswer: () => void;
	answer: string;
	answerType: string;
	letter: string;
	statistics: number | null;
	isLoading: boolean;
	renderAdditionalAction: ((letter: string) => React.ReactNode) | null;
	isDisabled: ((letter: string) => boolean) | null;
	'data-test-id'?: string;
}

export function Answer({
	setAnswer,
	answer,
	answerType,
	letter,
	statistics,
	isLoading,
	renderAdditionalAction,
	isDisabled,
	'data-test-id': dataTestId,
}: AnswerProps) {
	const { fontSizeClass, getFontSizeClass } = useFontSize();
	const className = answerType ? `answer--${answerType}` : "";

	const formatAnswer = (value: string): string => {
		const answer = formatHtmlTag(value);

		return answer.replace(/^[A-E]\.\s/, "").trim();
	};

	const renderLetter = () => {
		return (
			<div className="flex items-center justify-center gap-8">
				<Avatar
					className={cx(
						"square-28",
						className || "answer--default",
						"text-black size-14-tiny text-uppercase",
					)}
				>
					{isLoading ? (
						<CircularProgress
							sx={{ width: "50% !important", height: "50% !important" }}
						/>
					) : (
						letter
					)}
				</Avatar>
				{!!statistics && (
					<div
						className={cx("flex", {
							"text-success": answerType === AnswerType.CORRECT,
							"text-error": answerType === AnswerType.WRONG || !answerType,
							"text-warning": answerType === AnswerType.WARNING,
						})}
					>
						<span className={getFontSizeClass(-1)}>{`${statistics}%`}</span>
					</div>
				)}
			</div>
		);
	};

	const renderSecondaryAction = () => {
		if (!renderAdditionalAction) {
			return null;
		}

		return (
			<ListItemSecondaryAction>
				{renderAdditionalAction(letter)}
			</ListItemSecondaryAction>
		);
	};

	return (
		<ListItem
			secondaryAction={renderSecondaryAction()}
			className={cx(
				"p-0",
				css`
          .MuiListItemSecondaryAction-root {
            right: 4px !important;
          }
        `,
			)}
		>
			<ListItemButton
				onClick={setAnswer}
				className={cx(
					"list-item-answer gap-8 flex items-center cursor-pointer",
					className,
				)}
				disabled={isDisabled ? isDisabled(letter) : false}
				data-test-id={dataTestId}
			>
				<ListItemAvatar
					className={css`
            min-width: unset;
          `}
				>
					{renderLetter()}
				</ListItemAvatar>
				<ListItemText id={answer}>
					<div
						className={cx(fontSizeClass, "first-letter-upper")}
						dangerouslySetInnerHTML={{ __html: formatAnswer(answer) }}
					/>
				</ListItemText>
			</ListItemButton>
		</ListItem>
	);
}
